import React, { useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { submitPublicReview } from '../services/api';
import { trackAnalyticsEvent } from '../services/analytics';

interface Props {
  listingId: number;
  onClose: () => void;
}

const ReviewForm: React.FC<Props> = ({ listingId, onClose }) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [author, setAuthor] = useState('');
  const [authorEmail, setAuthorEmail] = useState('');
  const [text, setText] = useState('');
  const [r1, setR1] = useState(0);
  const [r2, setR2] = useState(0);
  const [r3, setR3] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const onSubmit = async () => {
    setError('');
    if (text.trim().length < 30) { setError('Review must be at least 30 characters.'); return; }
    if (!authorEmail) { setError('Please provide an email so we can verify your review.'); return; }
    setSubmitting(true);
    try {
      let recaptchaToken: string | undefined;
      if (executeRecaptcha) {
        try { recaptchaToken = await executeRecaptcha('submit_review'); }
        catch { recaptchaToken = undefined; }
      }
      await submitPublicReview({
        listingId,
        authorEmail: authorEmail.trim(),
        author: author.trim() || undefined,
        text: text.trim(),
        ratingTreatmentEffectiveness: r1 || undefined,
        ratingAccommodationsAmenities: r2 || undefined,
        ratingMealsNutrition: r3 || undefined,
        recaptchaToken,
      });
      trackAnalyticsEvent('review_submit', { placement: 'review_modal' });
      setDone(true);
    } catch (e: any) {
      console.error('[ReviewForm.submit] fail',
        'msg=', e?.message, 'code=', e?.code, 'name=', e?.name,
        'status=', e?.response?.status, 'data=', JSON.stringify(e?.response?.data),
        'url=', e?.config?.url, 'baseURL=', e?.config?.baseURL,
        'method=', e?.config?.method, 'isAxiosError=', e?.isAxiosError,
        'stack=', e?.stack);
      setError(e?.response?.data?.message ?? 'Could not submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <Modal onClose={onClose}>
        <h2>✅ Check your email</h2>
        <p style={{ color: '#666' }}>
          We sent a confirmation link to <strong>{authorEmail}</strong>. Click it to publish your review.
        </p>
        <button style={btn} onClick={onClose}>Done</button>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose}>
      <h2 style={{ marginTop: 0 }}>Write a review</h2>
      <p style={{ color: '#666', fontSize: 14 }}>
        Reviews help future patients make informed decisions. We'll send you an email to verify your address before publishing.
      </p>

      <label style={lbl}>Your name (optional)</label>
      <input style={inp} value={author} onChange={e => setAuthor(e.target.value)} placeholder="Anonymous" />

      <label style={lbl}>Your email</label>
      <input style={inp} type="email" value={authorEmail} onChange={e => setAuthorEmail(e.target.value)} placeholder="you@example.com" />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginTop: 16 }}>
        <RatingPicker label="Treatment effectiveness" value={r1} onChange={setR1} />
        <RatingPicker label="Accommodations" value={r2} onChange={setR2} />
        <RatingPicker label="Meals & nutrition" value={r3} onChange={setR3} />
      </div>

      <label style={lbl}>Your review</label>
      <textarea
        style={{ ...inp, minHeight: 140 }}
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder="Share your honest experience. What worked, what didn't, what other patients should know."
      />

      {error && <p style={{ color: '#c62828', marginTop: 12 }}>{error}</p>}

      <div style={{ marginTop: 20, display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
        <button style={btnGhost} onClick={onClose} disabled={submitting}>Cancel</button>
        <button style={btn} onClick={onSubmit} disabled={submitting}>
          {submitting ? 'Submitting…' : 'Submit review'}
        </button>
      </div>
    </Modal>
  );
};

const Modal: React.FC<{ onClose: () => void; children: React.ReactNode }> = ({ onClose, children }) => (
  <div
    role="dialog"
    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
    onClick={onClose}
  >
    <div
      onClick={e => e.stopPropagation()}
      style={{ background: '#fff', borderRadius: 12, padding: 28, maxWidth: 560, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}
    >
      {children}
    </div>
  </div>
);

const RatingPicker: React.FC<{ label: string; value: number; onChange: (v: number) => void }> = ({ label, value, onChange }) => (
  <div>
    <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>{label}</div>
    <div style={{ display: 'flex', gap: 4 }}>
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(value === n ? 0 : n)}
          style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, fontSize: 22, color: n <= value ? '#f6b100' : '#ddd' }}
          aria-label={`${n} stars`}
        >★</button>
      ))}
    </div>
  </div>
);

const lbl: React.CSSProperties = { display: 'block', marginTop: 14, marginBottom: 4, fontWeight: 600, fontSize: 13 };
const inp: React.CSSProperties = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d4d4d8', fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit' };
const btn: React.CSSProperties = { padding: '10px 20px', background: '#1a237e', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' };
const btnGhost: React.CSSProperties = { padding: '10px 20px', background: 'transparent', color: '#666', border: '1px solid #ccc', borderRadius: 8, cursor: 'pointer' };

export default ReviewForm;
